import { createRef, useEffect, useState } from 'react';
import { DriverFinancialModel } from '../../../models/driverFinancial';
import { DEFAULT_ROWS_PER_PAGE } from '../../../config-global';
import { DriverFinancialEnum } from '../../../enums/driverFinancailEnum';
import axios from '../../../lib/axios';
import { FIND_MANY_DRIVER_FINANCIAL } from '../../../lib/endpoints/driverFinancial';
import { convertDateStringToNormalString } from '../../../utils/convertDateFormat';
import { totalDistance } from '../../../utils/sumTotalDistance';

export const useDriverFinancialController = () => {
  const [data, setData] = useState<DriverFinancialModel[]>([]);
  const [selectedData, setSelectedData] = useState<DriverFinancialModel>(null!);
  const [selectedId, setSelectedId] = useState<string>(null!);
  const [page, setPage] = useState<number>(1);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [openMenu, setOpenMenu] = useState(null);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [tabDriverFinancial, setTabDriverFinancial] = useState<DriverFinancialEnum>(
    DriverFinancialEnum.NewInvoice
  );

  const buttonExportCsv = createRef<any>();

  const handleFetchData = async () => {
    try {
      setLoadingData(true);
      const responseData = await axios.post(FIND_MANY_DRIVER_FINANCIAL, {
        filter: {
          isPaidStatus: false,
          clear: true,
        },
      });
      setData(responseData?.data?.data);
    } catch (err) {
    } finally {
      setLoadingData(false);
    }
  };

  //? Function

  const handleSearch = (e: any) => {
    setSearchValue(e.target.value);
  };

  const handleOpenForm = () => {
    setOpenForm(true);
    const getData = data.filter((item: DriverFinancialModel) => item.id === selectedId)[0];
    setSelectedData(getData);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setOpenMenu(null);
  };

  const handleOpenMenu = (event: any, getId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedId(getId);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  //? Filter Data

  const allInvoice = data?.length;
  const totalAllInvoiceMoney = data?.reduce(
    (total, item) => total + (item?.totalMoney || 0),
    0
  );

  const allPayRider = data?.filter(
    (item: DriverFinancialModel) => item?.clear === true
  );
  const totalMoneyPayRider = allPayRider?.reduce(
    (total, item) => total + (item?.totalMoneyRiderGot || 0),
    0
  );

  const allPaidMoney = data?.filter(
    (item: DriverFinancialModel) => item?.isPaidStatus === true
  );
  const totalPaidMoney = allPaidMoney?.reduce((total, item) => total + (item?.totalMoney || 0), 0);

  const filterData: DriverFinancialModel[] = data.filter(
    (item: DriverFinancialModel) =>
      item.rider?.username?.toLowerCase()?.includes(searchValue.toLocaleLowerCase()) ||
      item.createdAt.toLowerCase()?.includes(searchValue.toLocaleLowerCase())
  );

  const rows = filterData.slice(
    (page - 1) * DEFAULT_ROWS_PER_PAGE,
    page * DEFAULT_ROWS_PER_PAGE + DEFAULT_ROWS_PER_PAGE
  );

  const isSearchNotFound: boolean = !filterData.length && !!searchValue;
  const countPagination = Math.ceil(filterData.length / DEFAULT_ROWS_PER_PAGE);

  const groupedData = data.reduce((acc: any, item: DriverFinancialModel) => {
    const username: any = item?.rider?.username;
    if (!acc[username]) {
      acc[username] = {
        driverNo: item?.rider?.driverNo,
        balanceSubmission: 0,
        totalMoneyRiderCantake: 0,
        totalMoneyRiderGot: 0,
        totalMoneyOrderReturn: 0,
        totalDistance: 0,
        totalMoney: 0,
        createdAt: item?.createdAt, // Assuming the same date for all entries of the same username
      };
    }

    const distanceOrderCash = item?.ordersCash?.orders?.map((order) => order?.distance) || [];
    const distanceOrderOnePay = item?.ordersOnePay?.orders?.map((order) => order?.distance) || [];
    const distanceOrderReturn = item?.ordersReturn?.orders?.map((order) => order?.distance) || [];
    const distanceRiderGot = totalDistance(
      distanceOrderCash,
      distanceOrderOnePay,
      distanceOrderReturn
    );

    acc[username].balanceSubmission += item?.balanceSubmission?.length || 0;
    acc[username].totalMoneyRiderCantake = item?.rider?.totalMoneyRiderCantake || 0;
    acc[username].totalMoneyOrderReturn = item?.rider?.totalMoneyOrderReturn || 0;
    acc[username].totalMoneyRiderGot = item?.totalMoneyRiderGot || 0;
    acc[username].totalDistance += distanceRiderGot;
    acc[username].totalMoney = item?.totalMoney || 0;

    return acc;
  }, {});

  const getDataExportCsv = Object.keys(groupedData).map((username) => {
    const item = groupedData[username];
    return {
      username,
      driverNo: item.driverNo,
      balanceSubmission: item.balanceSubmission,
      totalMoneyRiderGot: item.totalMoneyRiderGot,
      totalMoneyOrderReturn: item.totalMoneyOrderReturn,
      totalMoneyRiderCantake: item.totalMoneyRiderCantake,
      totalDistance: item.totalDistance,
      totalMoney: item.totalMoney,
      createdAt: convertDateStringToNormalString(item.createdAt),
    };
  });

  //? useEffect

  useEffect(() => {
    handleFetchData();
  }, []);  

  return {
    handleSearch,
    isSearchNotFound,
    handleChangePage,
    rows,
    countPagination,
    page,
    openMenu,
    openForm,
    handleOpenForm,
    handleCloseForm,
    handleOpenMenu,
    handleCloseMenu,
    searchValue,
    tabDriverFinancial,
    handleTabDriverFinancial: (newValue: DriverFinancialEnum) => setTabDriverFinancial(newValue),
    loadingData,
    selectedData,
    handleFetchData,
    data,
    totalAllInvoiceMoney,
    allInvoice,
    totalMoneyPayRider,
    allPayRider,
    allPaidMoney,
    totalPaidMoney,
    buttonExportCsv,
    getDataExportCsv,
  };
};
